/**
 * Temporal/4D evaluation — INIT-2026-Q3-SCENE-HIERARCHY close-out C6 (G6).
 *
 * Bridges a node's `temporalBindings` (emitted by the converter, e.g. the plant
 * extractor's valve-state / pump-speed channels) to a concrete `temporalState`
 * value driven by the viewer timeline. The 4D timeline scrub computes a state
 * per bound node and writes it into `ControlOverlay.byObject[id].temporalState`.
 *
 * There is no live time-series backend in dev (sourceRef is a `sim://` URI), so
 * the value is derived *deterministically* from the scrub time — enough to
 * demonstrate valve open/closed and pump-speed changes as the timeline moves.
 */

import type {
  TemporalBinding,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

/** Concrete shape stored in `ControlOverlayEntry.temporalState` (typed `unknown`). */
export interface TemporalStateValue {
  channelId: string;
  kind: TemporalBinding["kind"];
  value: string | number;
  /** The scrub time (epoch ms) this state was evaluated at. */
  at: number;
}

/** Demo cadence: state channels toggle every minute; scalars cycle every two. */
export const STATE_PERIOD_MS = 60_000;
export const SCALAR_PERIOD_MS = 120_000;

/**
 * Deterministically evaluate one binding at `timeMs`.
 * - `state`  → "open" / "closed" toggling on STATE_PERIOD_MS.
 * - `scalar` → integer 0..100 sawtooth over SCALAR_PERIOD_MS.
 * - `transform` → identity marker (0).
 */
export function evaluateTemporalBinding(
  binding: TemporalBinding,
  timeMs: number,
): TemporalStateValue {
  if (binding.kind === "state") {
    const phase = Math.floor(timeMs / STATE_PERIOD_MS) % 2;
    return {
      channelId: binding.channelId,
      kind: "state",
      value: phase === 0 ? "open" : "closed",
      at: timeMs,
    };
  }
  if (binding.kind === "scalar") {
    const m = ((timeMs % SCALAR_PERIOD_MS) + SCALAR_PERIOD_MS) % SCALAR_PERIOD_MS;
    return {
      channelId: binding.channelId,
      kind: "scalar",
      value: Math.round((m / SCALAR_PERIOD_MS) * 100),
      at: timeMs,
    };
  }
  return { channelId: binding.channelId, kind: "transform", value: 0, at: timeMs };
}

/**
 * Compute `{ objectId → TemporalStateValue }` for every node that carries a
 * temporal binding. Nodes without bindings are skipped (the result only holds
 * bound nodes). The first binding wins when a node has several.
 */
export function computeTemporalStates(
  nodes: readonly UniversalSceneNode[],
  timeMs: number,
): Record<string, TemporalStateValue> {
  const out: Record<string, TemporalStateValue> = {};
  for (const node of nodes) {
    const bindings = node.temporalBindings;
    if (bindings && bindings.length > 0) {
      out[node.objectId] = evaluateTemporalBinding(bindings[0], timeMs);
    }
  }
  return out;
}
